
import React from "react";
import { addDays, format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DateRangePickerProps {
  dateRange: DateRange;
  onUpdate: (range: DateRange) => void;
  className?: string;
  align?: "center" | "start" | "end";
}

export function DateRangePicker({
  dateRange,
  onUpdate,
  className,
  align = "start",
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Preset ranges
  const handleRangeSelection = (days: number) => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - days);
    
    onUpdate({ from, to: today });
    setIsOpen(false);
  };

  const formatDateRange = () => {
    if (!dateRange?.from) return "Pick a date range";
    
    if (dateRange.to) {
      if (isMobile) {
        return `${format(dateRange.from, "MMM dd")} - ${format(dateRange.to, "MMM dd")}`;
      }
      return `${format(dateRange.from, "LLL dd, y")} - ${format(dateRange.to, "LLL dd, y")}`;
    }
    
    return format(dateRange.from, isMobile ? "MMM dd, y" : "LLL dd, y");
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "justify-start text-left font-normal",
              isMobile ? "w-full h-11" : "w-[300px]",
              !dateRange && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
            <span className="truncate">{formatDateRange()}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0" 
          align={isMobile ? "center" : align}
          side={isMobile ? "bottom" : "bottom"}
        >
          <div className="flex flex-col space-y-2 p-2">
            <div className={cn(
              "grid gap-2",
              isMobile ? "grid-cols-1" : "grid-cols-2"
            )}>
              <Button 
                variant="outline" 
                className="text-xs h-8"
                onClick={() => handleRangeSelection(7)}
              >
                Last 7 days
              </Button>
              <Button
                variant="outline"
                className="text-xs h-8"
                onClick={() => handleRangeSelection(14)}
              >
                Last 14 days
              </Button>
              <Button
                variant="outline"
                className="text-xs h-8"
                onClick={() => handleRangeSelection(30)}
              >
                Last 30 days
              </Button>
              <Button
                variant="outline"
                className="text-xs h-8"
                onClick={() => handleRangeSelection(90)}
              >
                Last 90 days
              </Button>
            </div>

            <div className="border-t pt-2">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={onUpdate}
                numberOfMonths={isMobile ? 1 : 2}
                className="pointer-events-auto"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
